import { History } from "lucide-react";

import { cn } from "@/marketing/lib/utils";
import { Checkbox } from "@/marketing/components/ui/checkbox";
import { CAMPAIGN_OPERATIONS_PHASE_LABELS } from "@/marketing/models/campaignOperations";
import type { InfluencerCampaign } from "@/marketing/models/influencerCampaign";
import type { CampaignTrackerCellValue } from "./campaignOperationsPatches";
import { CampaignTrackerRows, type CampaignTrackerTraffic } from "./campaignTrackerRows";
import { TrackerCell } from "./TrackerCell";
import type { TrackerCellEditStart, TrackerCellSaveState } from "./trackerCellEdits";
import { TRACKER_GROUP_LABELS, TrackerColumns, type TrackerColumn, type TrackerColumnGroup, type TrackerComputedColumn } from "./trackerColumns";

interface CampaignTrackerGridProps {
  campaigns: InfluencerCampaign[];
  columns: TrackerColumn[];
  today: string;
  selectedIds: Set<string>;
  picOptions: string[];
  brandName: (brandId: string) => string | undefined;
  onToggle: (campaignId: string) => void;
  onToggleAll: () => void;
  onCommit: (campaign: InfluencerCampaign, field: string, value: CampaignTrackerCellValue, editStart: TrackerCellEditStart) => void;
  saveStateOf: (campaignId: string, field: string) => TrackerCellSaveState;
  onOpenCampaign: (campaign: InfluencerCampaign) => void;
  onShowHistory: (campaign: InfluencerCampaign) => void;
}

const GROUP_TONE: Record<TrackerColumnGroup, string> = {
  campaign: "tw-bg-slate-100 tw-text-slate-700",
  onboarding: "tw-bg-violet-50 tw-text-violet-800",
  content: "tw-bg-sky-50 tw-text-sky-800",
  government: "tw-bg-amber-50 tw-text-amber-800",
};

const TRAFFIC_TONE: Record<CampaignTrackerTraffic, string> = {
  red: "tw-text-red-700",
  yellow: "tw-text-amber-700",
  green: "tw-text-emerald-700",
  none: "tw-text-muted-foreground",
};

interface ColumnGroupSpan {
  group: TrackerColumnGroup;
  span: number;
}

function groupSpans(columns: TrackerColumn[]): ColumnGroupSpan[] {
  const spans: ColumnGroupSpan[] = [];
  for (const column of columns) {
    const last = spans[spans.length - 1];
    if (last && last.group === column.group) last.span += 1;
    else spans.push({ group: column.group, span: 1 });
  }
  return spans;
}

interface ComputedCellProps {
  campaign: InfluencerCampaign;
  column: TrackerComputedColumn;
  today: string;
  brandName: (brandId: string) => string | undefined;
  onOpenCampaign: (campaign: InfluencerCampaign) => void;
}

function ComputedCell({ campaign, column, today, brandName, onOpenCampaign }: ComputedCellProps) {
  const cellClass = "tw-border-b tw-border-r tw-border-border tw-px-2 tw-py-1 tw-align-top tw-text-xs";
  switch (column.computed) {
    case "deadline": {
      const deadline = CampaignTrackerRows.nextDeadline(campaign, today);
      const traffic = CampaignTrackerRows.traffic(campaign, today);
      if (campaign.operations?.cancelled) return <td className={cn(cellClass, "tw-text-muted-foreground")}>Cancelled</td>;
      if (!deadline) return <td className={cn(cellClass, "tw-text-muted-foreground")}>—</td>;
      const when = deadline.daysLeft < 0 ? `${-deadline.daysLeft}d overdue` : deadline.daysLeft === 0 ? "today" : `D-${deadline.daysLeft}`;
      return (
        <td className={cn(cellClass, "tw-whitespace-nowrap tw-font-medium", TRAFFIC_TONE[traffic])} title={deadline.dueDate}>
          {CAMPAIGN_OPERATIONS_PHASE_LABELS[deadline.phase]} · {when}
        </td>
      );
    }
    case "brand":
      return <td className={cellClass}>{brandName(campaign.brandId) ?? "—"}</td>;
    case "campaign":
      return (
        <td className={cellClass}>
          <button type="button" className="tw-text-left tw-font-medium hover:tw-underline" onClick={() => onOpenCampaign(campaign)}>
            {campaign.name}
          </button>
        </td>
      );
    case "status":
      return <td className={cn(cellClass, "tw-whitespace-nowrap")}>{campaign.status}</td>;
    case "type":
      return <td className={cellClass}>{campaign.types?.join(", ") || "—"}</td>;
    case "onboarded":
      return <td className={cn(cellClass, "tw-whitespace-nowrap")}>{campaign.onboardedCount ?? 0} / {campaign.onboardingTarget ?? "—"}</td>;
    case "posted":
      return <td className={cn(cellClass, "tw-whitespace-nowrap")}>{campaign.postedCount ?? 0} / {campaign.postTarget ?? "—"}</td>;
    case "pace": {
      const pace = CampaignTrackerRows.postingPace(campaign, today);
      return <td className={cn(cellClass, "tw-whitespace-nowrap")}>{pace ? (pace.remaining === 0 ? "Met" : `${pace.perDay}/day`) : "—"}</td>;
    }
  }
}

/** Master-sheet style grid: grouped headers, one row per campaign, every operations cell editable in place. */
export function CampaignTrackerGrid({ campaigns, columns, today, selectedIds, picOptions, brandName, onToggle, onToggleAll, onCommit, saveStateOf, onOpenCampaign, onShowHistory }: CampaignTrackerGridProps) {
  const allSelected = campaigns.length > 0 && campaigns.every((campaign) => selectedIds.has(campaign.campaignId));
  return (
    <div className="tw-h-full tw-overflow-auto tw-rounded-lg tw-border tw-border-border">
      <table className="tw-min-w-max tw-border-separate tw-border-spacing-0 tw-text-left">
        <thead className="tw-sticky tw-top-0 tw-z-20 tw-bg-background">
          <tr>
            <th className="tw-sticky tw-left-0 tw-z-30 tw-border-b tw-border-r tw-border-border tw-bg-background" colSpan={2} />
            {groupSpans(columns).map((span, index) => (
              <th key={`${span.group}-${index}`} colSpan={span.span} className={cn("tw-border-b tw-border-r tw-border-border tw-px-2 tw-py-1.5 tw-text-[11px] tw-font-semibold", GROUP_TONE[span.group])}>
                {TRACKER_GROUP_LABELS[span.group]}
              </th>
            ))}
          </tr>
          <tr className="tw-bg-muted">
            <th className="tw-sticky tw-left-0 tw-z-30 tw-w-8 tw-border-b tw-border-r tw-border-border tw-bg-muted tw-px-2">
              <Checkbox aria-label="Select all campaigns" checked={allSelected} onCheckedChange={onToggleAll} />
            </th>
            <th className="tw-border-b tw-border-r tw-border-border tw-bg-muted tw-px-1" aria-label="History" />
            {columns.map((column) => (
              <th key={column.id} scope="col" className="tw-min-w-[110px] tw-max-w-[220px] tw-border-b tw-border-r tw-border-border tw-px-2 tw-py-1.5 tw-text-[11px] tw-font-semibold tw-text-muted-foreground">
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {campaigns.map((campaign) => (
            <tr key={campaign.campaignId} data-testid="tracker-row" data-campaign-id={campaign.campaignId} className={cn(selectedIds.has(campaign.campaignId) && "tw-bg-primary/5")}>
              <td className="tw-sticky tw-left-0 tw-z-10 tw-border-b tw-border-r tw-border-border tw-bg-background tw-px-2 tw-align-top">
                <Checkbox aria-label={`Select ${campaign.name}`} checked={selectedIds.has(campaign.campaignId)} onCheckedChange={() => onToggle(campaign.campaignId)} className="tw-mt-1" />
              </td>
              <td className="tw-border-b tw-border-r tw-border-border tw-px-1 tw-align-top">
                <button type="button" aria-label={`History of ${campaign.name}`} className="tw-mt-1 tw-text-muted-foreground hover:tw-text-foreground" onClick={() => onShowHistory(campaign)}>
                  <History className="tw-h-3.5 tw-w-3.5" />
                </button>
              </td>
              {columns.map((column) =>
                TrackerColumns.isEditable(column) ? (
                  <TrackerCell
                    key={column.id}
                    campaign={campaign}
                    column={column}
                    picOptions={picOptions}
                    readSaveState={() => saveStateOf(campaign.campaignId, column.field)}
                    onCommit={onCommit}
                  />
                ) : (
                  <ComputedCell key={column.id} campaign={campaign} column={column} today={today} brandName={brandName} onOpenCampaign={onOpenCampaign} />
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
